import React, { useState } from 'react';
import axios from 'axios';
import { Box, Typography, TextField, Button, Table, TableHead, TableBody, TableRow, TableCell, CircularProgress, Chip, Tab, Tabs } from '@mui/material';

const cellSx = { color: '#888', fontSize: 11, fontFamily: 'monospace', py: 0.5, borderColor: '#1e1e1e' };
const headSx = { ...cellSx, color: '#555', fontSize: 10, letterSpacing: 1 };

function TagBrowser() {
  const [studyUID, setStudyUID] = useState('');
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');

  const fetchTags = async () => {
    if (!studyUID.trim()) return;
    setLoading(true); setError(''); setTags([]);
    try {
      const res = await axios.get(`/api/admin/dicom/metadata?study_uid=${encodeURIComponent(studyUID.trim())}`);
      setTags(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  const filtered = tags.filter(t => !filter || t.tag.includes(filter) || t.value.toLowerCase().includes(filter.toLowerCase()));

  return (
    <Box>
      <Typography sx={{ color: '#555', fontSize: 10, letterSpacing: 2, mb: 1.5 }}>TAG BROWSER</Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField size="small" placeholder="Study Instance UID" value={studyUID}
          onChange={e => setStudyUID(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && fetchTags()}
          sx={{ flex: 1, '& input': { color: '#e0e0e0', fontSize: 12, fontFamily: 'monospace' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2a2a2a' } }} />
        <Button variant="outlined" onClick={fetchTags} disabled={loading}
          sx={{ borderColor: '#2a2a2a', color: '#888', fontSize: 11 }}>
          {loading ? <CircularProgress size={14} /> : 'Fetch'}
        </Button>
      </Box>
      {tags.length > 0 && (
        <TextField size="small" placeholder="Filter tags..." value={filter}
          onChange={e => setFilter(e.target.value)}
          sx={{ mb: 1.5, width: 300, '& input': { color: '#e0e0e0', fontSize: 11 }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#1e1e1e' } }} />
      )}
      {error && <Typography sx={{ color: '#f44336', fontSize: 11, mb: 1 }}>{error}</Typography>}
      {filtered.length > 0 && (
        <Box sx={{ overflow: 'auto', maxHeight: 500, border: '1px solid #1e1e1e', borderRadius: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={headSx}>TAG</TableCell>
                <TableCell sx={headSx}>VR</TableCell>
                <TableCell sx={headSx}>VALUE</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((t, i) => (
                <TableRow key={i} sx={{ '&:hover': { bgcolor: '#0d1a1a' } }}>
                  <TableCell sx={{ ...cellSx, color: '#00bcd4', width: 120 }}>{t.tag}</TableCell>
                  <TableCell sx={{ ...cellSx, width: 50 }}>{t.vr}</TableCell>
                  <TableCell sx={{ ...cellSx, color: '#e0e0e0' }}>{t.value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      {!loading && tags.length > 0 && <Typography sx={{ color: '#444', fontSize: 10, mt: 0.5 }}>{filtered.length} / {tags.length} tags</Typography>}
    </Box>
  );
}

function DicomPing() {
  const [form, setForm] = useState({ ae_title: 'DCM4CHEE', host: 'arc', port: '11112' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const ping = async () => {
    setLoading(true); setResult(null);
    try {
      const res = await axios.post('/api/admin/dicom/echo', { ...form, port: parseInt(form.port) });
      setResult(res.data);
    } catch (e) {
      setResult({ success: false, error: e.message });
    } finally { setLoading(false); }
  };

  const fieldSx = { '& input': { color: '#e0e0e0', fontSize: 12 }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2a2a2a' }, '& .MuiInputLabel-root': { color: '#555', fontSize: 11 } };

  return (
    <Box>
      <Typography sx={{ color: '#555', fontSize: 10, letterSpacing: 2, mb: 1.5 }}>DICOM PING (C-ECHO)</Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <TextField size="small" label="AE Title" value={form.ae_title} onChange={e => setForm(f => ({ ...f, ae_title: e.target.value }))} sx={{ width: 140, ...fieldSx }} />
        <TextField size="small" label="Host" value={form.host} onChange={e => setForm(f => ({ ...f, host: e.target.value }))} sx={{ width: 180, ...fieldSx }} />
        <TextField size="small" label="Port" value={form.port} onChange={e => setForm(f => ({ ...f, port: e.target.value }))} sx={{ width: 80, ...fieldSx }} />
        <Button variant="outlined" onClick={ping} disabled={loading} sx={{ borderColor: '#2a2a2a', color: '#888', fontSize: 11, alignSelf: 'center' }}>
          {loading ? <CircularProgress size={14} /> : 'Ping'}
        </Button>
      </Box>
      {result && (
        <Box sx={{ bgcolor: '#0a0a0a', border: `1px solid ${result.success ? '#1b5e20' : '#b71c1c'}`, borderRadius: 1, p: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Chip label={result.success ? 'SUCCESS' : 'FAILED'} size="small"
              sx={{ bgcolor: result.success ? '#1b5e20' : '#b71c1c', color: '#fff', fontSize: 10 }} />
            {result.response_ms !== undefined && <Typography sx={{ color: '#4caf50', fontSize: 12, fontFamily: 'monospace' }}>{result.response_ms}ms</Typography>}
          </Box>
          {result.error && <Typography sx={{ color: '#f44336', fontSize: 11 }}>{result.error}</Typography>}
          {result.note && <Typography sx={{ color: '#888', fontSize: 11 }}>{result.note}</Typography>}
        </Box>
      )}
      <Box sx={{ mt: 2 }}>
        <Typography sx={{ color: '#333', fontSize: 10, mb: 1 }}>QUICK SELECT</Typography>
        {[{ ae: 'DCM4CHEE', host: 'arc', port: 11112, desc: 'Local dcm4chee-arc' }].map(ae => (
          <Button key={ae.ae} size="small" variant="text"
            onClick={() => setForm({ ae_title: ae.ae, host: ae.host, port: String(ae.port) })}
            sx={{ color: '#555', fontSize: 10, textTransform: 'none', fontFamily: 'monospace' }}>
            {ae.ae}@{ae.host}:{ae.port} — {ae.desc}
          </Button>
        ))}
      </Box>
    </Box>
  );
}

export default function DicomTools() {
  const [tab, setTab] = useState(0);
  return (
    <Box sx={{ p: 3, maxWidth: 1000 }}>
      <Typography sx={{ color: 'primary.main', fontSize: 11, letterSpacing: 2, mb: 2, fontFamily: 'monospace' }}>DICOM TOOLS</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: '1px solid #1e1e1e',
        '& .MuiTab-root': { color: '#555', fontSize: 10, letterSpacing: 1 },
        '& .Mui-selected': { color: 'primary.main' },
        '& .MuiTabs-indicator': { bgcolor: 'primary.main' } }}>
        <Tab label="TAG BROWSER" />
        <Tab label="DICOM PING" />
      </Tabs>
      {tab === 0 && <TagBrowser />}
      {tab === 1 && <DicomPing />}
    </Box>
  );
}
